import React from 'react'

import { tva } from '../../tailwind/merge'
import { colors } from '../../tokens'
import { Box } from '../box'
import { Icon } from '../icon'
import { type IconSource } from '../icon/Icon.types'

export type UnitTaskTypeIconTone = 'red' | 'green' | 'grey' | 'silver' | 'transparent'
export type UnitTaskTypeIconBorder = 'none' | 'solid' | 'dashed'

/** Figma draws the glyph at 24px inside the 36px circle. */
export const UNIT_TASK_TYPE_GLYPH_SIZE = 24

const unitTaskTypeIconFrame = tva({
    base: 'shrink-0 items-center justify-center',
})

const unitTaskTypeIcon = tva({
    base: 'items-center justify-center overflow-hidden rounded-full',
    variants: {
        tone: {
            red: 'bg-graphics-red',
            green: 'bg-graphics-green',
            grey: 'bg-graphics-dark-grey',
            silver: 'bg-graphics-silver',
            transparent: 'bg-transparent',
        },
        // NativeWind disables Preflight, so a bare `border` is invisible — always
        // pair the width with an explicit style + colour.
        border: {
            none: '',
            solid: 'border border-solid border-content-default',
            dashed: 'border border-dashed border-content-default',
        },
    },
})

/**
 * Glyph ink per tone, from Figma AOS-1248: bright fills take the inverted dark
 * ink, dark and transparent ones take the light default.
 *
 * Resolved values rather than utility classes on purpose. React Native has no
 * CSS cascade, so a colour class set here never reaches the glyph, and every
 * badge on mobile painted its icon black.
 */
const glyphInk: Record<UnitTaskTypeIconTone, string> = {
    red: colors.content.inverted,
    green: colors.content.inverted,
    silver: colors.content.inverted,
    grey: colors.content.default,
    transparent: colors.content.default,
}

export type UnitTaskTypeIconProps = {
    /** Circle fill (Figma role colours: command=red, present/coming=green, …). */
    tone?: UnitTaskTypeIconTone
    /** Ring style — `dashed` for pending, `solid` for rejected. */
    border?: UnitTaskTypeIconBorder
    /** Outer frame in px (Figma is 44); the circle is 8px smaller. */
    size?: number
    /** Centred glyph — the component owns its 24px size and its ink. */
    glyph?: IconSource
    /** Alternative centred content, for the avatar-photo variant. */
    children?: React.ReactNode
    className?: string
    testID?: string
    accessibilityLabel?: string
}

/**
 * Unit Task List participant type icon (Figma `UnitTaskListIcon`, node
 * 10779:89519): a 36px circle in a role colour (or transparent with a ring),
 * holding a 24px glyph, centred in a 44px frame. The 4px inset is part of the
 * symbol, so the frame keeps the layout footprint the design specifies.
 *
 * Presentational — the role→tone/border/glyph mapping lives in the caller (the
 * live `ParticipantStatusPictogram`), so the atom stays free of domain enums.
 */
export const UnitTaskTypeIcon: React.FC<UnitTaskTypeIconProps> = ({
    tone = 'transparent',
    border = 'none',
    size = 44,
    glyph,
    children,
    className,
    testID,
    accessibilityLabel,
}) => (
    <Box
        className={unitTaskTypeIconFrame({ class: className })}
        style={{ width: size, height: size }}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
    >
        <Box
            className={unitTaskTypeIcon({ tone, border })}
            style={{ width: size - 8, height: size - 8 }}
        >
            {glyph ? (
                <Icon as={glyph} size={UNIT_TASK_TYPE_GLYPH_SIZE} color={glyphInk[tone]} />
            ) : (
                children
            )}
        </Box>
    </Box>
)

UnitTaskTypeIcon.displayName = 'UnitTaskTypeIcon'
